import { conIva, formatFecha } from "../shared";

// Fechas con el mismo formato que el resto de la plataforma (admin): se usa el
// formatFecha compartido; sin fecha, un guion
export function fechaCorta(fecha) {
  return fecha ? formatFecha(fecha) : "-";
}

// Días enteros desde una fecha hasta hoy (positivo si ya pasó)
export function diasDesde(fecha) {
  if (!fecha) return null;
  const ms = Date.now() - new Date(fecha).getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

// Días enteros desde hoy hasta una fecha (negativo si ya pasó)
export function diasHasta(fecha) {
  if (!fecha) return null;
  const ms = new Date(fecha).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

// Hace N días, en texto
export function haceDias(fecha) {
  const d = diasDesde(fecha);
  if (d === null) return "";
  if (d === 0) return "hoy";
  if (d === 1) return "hace 1 día";
  return `hace ${d} días`;
}

// Monto con símbolo, IVA incluido si corresponde
export function montoFactura(factura, iva) {
  const simbolo = factura.moneda?.simbolo || "";
  const monto = factura.masIva ? conIva(factura.monto, iva) : factura.monto;
  return { simbolo, monto: Number(monto) || 0 };
}

// Qué se factura: los procesos o, si no hay, las solicitudes de las propuestas
export function descripcionFactura(factura) {
  const items = (factura.procesos || []).map((p) => p.descripcion).filter(Boolean);
  const lista =
    items.length > 0
      ? items
      : (factura.propuestas || [])
          .map((p) => p.solicitud?.nombrefantasia)
          .filter(Boolean);
  if (lista.length === 0) return "-";
  return lista.slice(0, 2).join(", ") + (lista.length > 2 ? "…" : "");
}

// Estado de una factura, con la misma regla que el portal anterior
export function estadoFactura(factura) {
  if (factura.idNotaDeCreditoBiller || factura.anulada)
    return { clave: "anulada", texto: "Anulada", severidad: "secondary" };
  if (factura.fechaPago)
    return { clave: "pagada", texto: "Pagada", severidad: "success" };
  // Impaga: ámbar hasta 25 días desde la emisión; después, rojo (el mismo de
  // los hallazgos pendientes)
  const dias = diasDesde(factura.fechaCreacion);
  return {
    clave: "pendiente",
    texto: "Pendiente de pago",
    severidad: dias !== null && dias > 25 ? "danger" : "warning"
  };
}

// Pasos de una actividad en el orden real del flujo: desde el paso inicial,
// siguiendo las transiciones "aprobado" (el campo orden no alcanza: dos pasos
// seguidos pueden compartirlo). Con pasos en paralelo se recorre por niveles.
// Los pasos que el camino no alcanza (p. ej. datos viejos) van al final.
function pasosEnOrden(actividad) {
  const pasos = actividad.actividadpaso || [];
  const porId = new Map(pasos.map((p) => [p.id, p]));
  const siguientes = new Map();
  (actividad.transiciones || []).forEach((t) => {
    if (!t.destinoPasoId) return;
    if (!siguientes.has(t.origenPasoId)) siguientes.set(t.origenPasoId, []);
    siguientes.get(t.origenPasoId).push(t.destinoPasoId);
  });
  const orden = [];
  const vistos = new Set();
  let nivel = porId.has(actividad.pasoInicialId) ? [actividad.pasoInicialId] : [];
  while (nivel.length) {
    const proximo = [];
    nivel.forEach((id) => {
      if (vistos.has(id) || !porId.has(id)) return;
      vistos.add(id);
      orden.push(porId.get(id));
      proximo.push(...(siguientes.get(id) || []));
    });
    nivel = proximo;
  }
  pasos.filter((p) => !vistos.has(p.id)).forEach((p) => orden.push(p));
  return orden;
}

// Avance de un proceso medido en pasos, que es lo que le dice al cliente
// dónde está y cuánto falta (endpoint /pendientes y detalle del proceso).
// Una actividad finalizada (fechafinalizacion, el mismo criterio del admin)
// tiene todos sus pasos hechos; en una abierta, los pasos anteriores al actual
// están hechos y los posteriores pendientes. Una actividad sin pasos cuenta
// como un paso.
export function avanceProceso(proceso) {
  const nombreActividad = (a) => a?.tipoNombre || a?.actividadtipo?.nombre || "Actividad";
  const actividades = (proceso.actividad || []).map((a) => {
    const finalizada = !!a.fechafinalizacion;
    const enOrden = pasosEnOrden(a);
    const indiceActual = finalizada ? -1 : enOrden.findIndex((p) => p.actual);
    const pasos = enOrden.map((p, i) => ({
      id: p.id,
      nombre: p.nombre || "Paso",
      fechaInicioPaso: p.fechaInicioPaso,
      estado: finalizada
        ? "hecho"
        : i === indiceActual
        ? "actual"
        : indiceActual > -1 && i < indiceActual
        ? "hecho"
        : "pendiente"
    }));
    return {
      id: a.id,
      nombre: nombreActividad(a),
      finalizada,
      fechafinalizacion: a.fechafinalizacion,
      fechacreacion: a.fechacreacion,
      pasos,
      pasoActual: pasos[indiceActual] || null,
      numeroPaso: indiceActual + 1
    };
  });

  const actual = actividades.find((a) => !a.finalizada) || null;
  if (actual) actual.actual = true;
  const pasosDe = (a) => Math.max(a.pasos.length, 1);
  const hechosDe = (a) =>
    a.finalizada ? pasosDe(a) : a.pasos.filter((p) => p.estado === "hecho").length;
  const total = actividades.reduce((s, a) => s + pasosDe(a), 0);
  const finalizados = actividades.reduce((s, a) => s + hechosDe(a), 0);

  return {
    // En pasos (todas las actividades)
    total,
    finalizados,
    porcentaje: total ? Math.round((finalizados / total) * 100) : 0,
    actividades,
    actividadesFinalizadas: actividades.filter((a) => a.finalizada).length,
    todasFinalizadas: actividades.length > 0 && !actual,
    // Dónde está ahora: actividad, paso y "paso N de M" dentro de la actividad
    etapaActual: actual ? actual.nombre : null,
    pasoActual: actual?.pasoActual?.nombre || null,
    numeroPaso: actual?.pasoActual ? actual.numeroPaso : null,
    pasosEtapa: actual ? actual.pasos.length : null,
    diasEnEtapa: actual
      ? diasDesde(actual.pasoActual?.fechaInicioPaso || actual.fechacreacion)
      : null
  };
}

// Severidad (Tag de PrimeReact) de un certificado según su estado y vencimiento
export function estadoCertificado(cert) {
  const nombre = cert.estadocertificado?.nombre || "Vigente";
  const vigente = cert.estadocertificado ? cert.estadocertificado.vigentebool : true;
  const dias = diasHasta(cert.fechavencimiento);
  if (vigente) {
    if (dias !== null && dias <= 60)
      return { clave: "porVencer", texto: "Por vencer", severidad: "warning", dias };
    return { clave: "vigente", texto: nombre, severidad: "success", dias };
  }
  const severidades = { 2: "warning", 3: "danger", 4: "secondary" };
  return {
    clave: "noVigente",
    texto: nombre,
    severidad: severidades[cert.estadocertificado?.id] || "secondary",
    dias
  };
}

// Íconos iguales a los de las mismas etiquetas en otras secciones: "Pendiente
// de tu parte" (Inicio), "En revisión por" (solicitudes) y el tilde de lo
// resuelto (propuestas aceptadas)
export const ESTADOS_HALLAZGO = {
  // Rojo, igual que "Pendiente de tu parte" en el Inicio: le toca al cliente
  PENDIENTE_RESPUESTA: { texto: "Pendiente de respuesta", severidad: "danger", icono: "pi pi-exclamation-triangle" },
  EN_REVISION: { texto: "En revisión por el organismo", severidad: "secondary", icono: "pi pi-hourglass" },
  ABIERTO: { texto: "Abierto", severidad: "secondary" },
  CERRADO: { texto: "Cerrado", severidad: "success", icono: "pi pi-check" }
};

// Texto del estado de un hallazgo con el nombre del organismo (useOrganismo)
export function textoEstadoHallazgo(estado, organismo) {
  if (estado === "EN_REVISION") return `En revisión por ${organismo}`;
  return ESTADOS_HALLAZGO[estado]?.texto;
}

// Color de un tipo de hallazgo. Los tipos (cuántos y cómo se llaman) los
// configura cada organismo en el admin, así que el color sale de su propia
// configuración y no del nombre: el cliente tiene que responder → amarillo
// (requiere acción); el resto (p. ej. observaciones informativas) → neutro.
// Así cualquier tipo nuevo se ve coherente sin tocar código.
export function severidadTipoHallazgo(tipo) {
  if (tipo?.clienteResponde) return "warning";
  return "secondary";
}

// Textos del avance para tarjetas y tablas: "Paso 2 de 3: Ingreso de datos"
// y, sin actividad abierta, si ya terminaron todas o todavía no empezó ninguna.
export function textoPaso(avance) {
  if (!avance.pasoActual) return null;
  return `Paso ${avance.numeroPaso} de ${avance.pasosEtapa}: ${avance.pasoActual}`;
}
export function textoSinEtapa(avance) {
  return avance.todasFinalizadas ? "Actividades finalizadas" : "Sin actividades iniciadas";
}

// Documentos de una factura que el cliente puede descargar.
// De Biller o, para organismos que no lo usan, el archivo que subió el
// organismo (la API sirve uno u otro). El recibo subido a mano es
// archivoComprobantePagoId.
export function comprobantesFactura(f) {
  return {
    factura: !!(f.idFacturaBiller || f.archivoFacturaId),
    recibo: !!(f.idReciboBiller || f.archivoComprobantePagoId),
    notaCredito: !!(f.idNotaDeCreditoBiller || f.archivoNotaCreditoId)
  };
}

export const conMayuscula = (texto = "") => texto.charAt(0).toUpperCase() + texto.slice(1);
